import { useCallback } from 'react'
import axios from 'axios'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { extractErrorMessage } from '../utils/apitools'
import useAccount from './useAccount'
import useSnackbar from './useSnackbar'
import { API_BASE_URL } from '../constants/system'
import { Dashboard } from '../types/gotypes'

interface ListDashboardTemplatesResponse {
  customer: string
  templates: Dashboard[]
  count: number
}

/**
 * Personal + company-level templates (#971 Phase 2) — `GET/POST
 * /dashboards/:customer/templates`. Deliberately separate from
 * `useDashboards`: templates are customer-wide, not job-scoped (they carry no
 * data, so there is nothing to scope by), and must never appear in the
 * regular dashboards listing that hook drives. System templates stay in
 * systemTemplates.ts — this hook only covers the two stored tiers.
 *
 * Promote/demote/delete on a template row reuse `useDashboards`'
 * promoteDashboard/demoteDashboard/deleteDashboard as-is (same generic
 * endpoints, no template-specific behaviour on the client side) — callers
 * that need those should keep using `useDashboards`.
 */
export const useDashboardTemplates = ({ customer }: { customer: string }) => {
  const account = useAccount()
  const snackbar = useSnackbar()
  const queryClient = useQueryClient()

  const listQueryKey = ['dashboard-templates', { customer }]

  const listQuery = useQuery({
    queryKey: listQueryKey,
    enabled: Boolean(customer && account.user),
    queryFn: async () => {
      const res = await axios.get<ListDashboardTemplatesResponse>(
        `${API_BASE_URL}/dashboards/${customer}/templates`
      )
      return res.data.templates
    },
  })

  const createMutation = useMutation({
    mutationFn: async ({ sourceDashboardId, name, tags }: { sourceDashboardId: string; name: string; tags?: string[] }) => {
      const res = await axios.post<Dashboard>(`${API_BASE_URL}/dashboards/${customer}/templates`, {
        source_dashboard_id: sourceDashboardId,
        name,
        tags: tags || [],
      })
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listQueryKey })
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  const createTemplate = useCallback(
    async (args: { sourceDashboardId: string; name: string; tags?: string[] }) => {
      return await createMutation.mutateAsync(args)
    },
    [createMutation]
  )

  return {
    templates: listQuery.data || [],
    templatesLoading: listQuery.isLoading,
    templatesIsError: listQuery.isError,

    createTemplate,
    createTemplateIsLoading: createMutation.isPending,
  }
}

export default useDashboardTemplates
